/**
 * Trade balance math for the offer-composition screens.
 *
 * Two separate readings, deliberately kept apart:
 *
 *   getListedBalance — compares the two listed prices. Drives the badge on the
 *     pedal tiles. Every listing has a price, so every tile gets a verdict, and
 *     it's the same number both people see on the listings.
 *
 *   getTradeBalance — compares Reverb market values. Feeds the "By Reverb
 *     market" line in the offer summary, alongside the listed-price line, so
 *     the two are shown as separate opinions rather than blended into one.
 *
 * Market data is NOT mixed into the tile badge. Reverb ranges can be well off
 * for a given pedal (a model search picks up variants and other eras), and a
 * badge that silently overrides the visible listed prices with a bad range is
 * worse than no guidance at all — the recipient counters, and the user learns
 * our numbers can't be trusted.
 */

// Within this much of the other side, call it even rather than nickel-and-diming.
const FAIR_TOLERANCE_PCT = 0.1;
const FAIR_TOLERANCE_MIN_CENTS = 2000; // $20

// Cash suggestions round to $5 so they read as an estimate, not a quote.
const CASH_ROUNDING_CENTS = 500;

function midOrNull(tier) {
  return tier && Number.isFinite(tier.mid) ? tier.mid : null;
}

function priceCentsOrNull(dollars) {
  const n = Number(dollars);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
}

/**
 * @param {number} diff cents, positive when their side is worth more
 * @param {number} theirTotal cents, what the tolerance band is scaled against
 * @returns {{verdict: 'fair'|'add'|'ask', amountCents: number}}
 */
function balanceFromDiff(diff, theirTotal) {
  const tolerance = Math.max(
    FAIR_TOLERANCE_MIN_CENTS,
    Math.round(theirTotal * FAIR_TOLERANCE_PCT)
  );
  if (Math.abs(diff) <= tolerance) return { verdict: 'fair', amountCents: 0 };

  const rounded = Math.round(Math.abs(diff) / CASH_ROUNDING_CENTS) * CASH_ROUNDING_CENTS;
  return {
    verdict: diff > 0 ? 'add' : 'ask',
    amountCents: Math.max(CASH_ROUNDING_CENTS, rounded)
  };
}

/**
 * Balance by listed price — what both people see on the listings.
 *
 * @param {Array<number|string>} myPrices listed dollar amounts being offered
 * @param {number|string} theirPrice listed dollar amount being requested
 * @returns {{verdict: 'fair'|'add'|'ask', amountCents: number}|null}
 *          'add' = you should add cash, 'ask' = you can ask for cash.
 */
export function getListedBalance(myPrices, theirPrice) {
  const theirTotal = priceCentsOrNull(theirPrice);
  if (theirTotal === null) return null;

  const prices = Array.isArray(myPrices) ? myPrices : [myPrices];
  if (!prices.length) return null;

  let myTotal = 0;
  for (const price of prices) {
    const cents = priceCentsOrNull(price);
    if (cents === null) return null;
    myTotal += cents;
  }

  return balanceFromDiff(theirTotal - myTotal, theirTotal);
}

/**
 * Balance by Reverb market value. Null if either side is missing market data —
 * a partial total can't produce an honest verdict.
 *
 * @param {Array<object|null|undefined>} myTiers market tiers for the pedals being offered
 * @param {object|null|undefined} theirTier market tier for the pedal being requested
 * @returns {{verdict: 'fair'|'add'|'ask', amountCents: number}|null}
 */
export function getTradeBalance(myTiers, theirTier) {
  const theirMid = midOrNull(theirTier);
  if (theirMid === null) return null;

  const tiers = Array.isArray(myTiers) ? myTiers : [myTiers];
  if (!tiers.length) return null;

  let myTotal = 0;
  for (const tier of tiers) {
    const mid = midOrNull(tier);
    if (mid === null) return null;
    myTotal += mid;
  }

  return balanceFromDiff(theirMid - myTotal, theirMid);
}

export function formatBalanceLabel(balance) {
  if (!balance) return null;
  if (balance.verdict === 'fair') return 'Even trade';
  const dollars = Math.round(balance.amountCents / 100);
  return balance.verdict === 'add' ? `Suggested add: $${dollars}` : `Suggested ask: $${dollars}`;
}

/**
 * Class for the ribbon across the bottom of a selection thumbnail. Mirrors the
 * "Potential Match" banner on browse so the two read as one system. All
 * verdicts share a color, so the class doesn't vary — callers still route
 * through here so the markup has a single source for the name.
 */
export function getBalanceBadgeClass(balance) {
  if (!balance) return null;
  return 'trade-balance-thumbnail-badge';
}

export function getBalanceColors(balance) {
  if (!balance) return null;
  if (balance.verdict === 'fair') return { bg: '#ecfdf5', border: '#a7f3d0', text: '#047857' };
  if (balance.verdict === 'add') return { bg: '#fffbeb', border: '#fde68a', text: '#b45309' };
  return { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' };
}
